import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';

/**
 * Polyfills navigator.geolocation with Capacitor's Geolocation plugin
 * on native platforms (Android/iOS). This allows Mapbox's GeolocateControl
 * to work transparently in Capacitor apps.
 * 
 * Must be called BEFORE initializing the Mapbox map.
 */
export async function initCapacitorGeolocation(): Promise<boolean> {
  // Only polyfill on native platforms
  if (!Capacitor.isNativePlatform()) {
    console.log('[Geolocation] Running on web, using native browser geolocation');
    return true;
  }

  console.log('[Geolocation] Running on native platform, initializing Capacitor geolocation');

  try {
    // Check current permission status
    let permStatus = await Geolocation.checkPermissions();
    console.log('[Geolocation] Current permission status:', permStatus.location);

    // Request permissions if not granted
    if (permStatus.location === 'prompt' || permStatus.location === 'prompt-with-rationale') {
      console.log('[Geolocation] Requesting permissions...');
      permStatus = await Geolocation.requestPermissions({ permissions: ['location'] });
      console.log('[Geolocation] Permission result:', permStatus.location);
    }

    if (permStatus.location === 'denied') {
      console.warn('[Geolocation] Location permission denied');
      return false;
    }

    // Polyfill navigator.geolocation for Mapbox compatibility
    polyfillNavigatorGeolocation();
    
    return true;
  } catch (error) {
    console.error('[Geolocation] Error initializing:', error);
    return false;
  }
}

/**
 * Polyfills navigator.geolocation with Capacitor's implementation
 */
function polyfillNavigatorGeolocation(): void {
  const watchCallbacks = new Map<number, string>();
  let watchIdCounter = 1;

  const capacitorGeolocation: Geolocation = {
    getCurrentPosition: (
      successCallback: PositionCallback,
      errorCallback?: PositionErrorCallback | null,
      options?: PositionOptions
    ): void => {
      Geolocation.getCurrentPosition({
        enableHighAccuracy: options?.enableHighAccuracy ?? true,
        timeout: options?.timeout ?? 10000,
        maximumAge: options?.maximumAge ?? 0,
      })
        .then((position) => {
          successCallback({
            coords: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              altitude: position.coords.altitude,
              altitudeAccuracy: position.coords.altitudeAccuracy,
              heading: position.coords.heading,
              speed: position.coords.speed,
            },
            timestamp: position.timestamp,
          } as GeolocationPosition);
        })
        .catch((error) => {
          console.error('[Geolocation] getCurrentPosition error:', error);
          if (errorCallback) {
            errorCallback({
              code: GeolocationPositionError.POSITION_UNAVAILABLE,
              message: error.message || 'Position unavailable',
              PERMISSION_DENIED: GeolocationPositionError.PERMISSION_DENIED,
              POSITION_UNAVAILABLE: GeolocationPositionError.POSITION_UNAVAILABLE,
              TIMEOUT: GeolocationPositionError.TIMEOUT,
            } as GeolocationPositionError);
          }
        });
    },

    watchPosition: (
      successCallback: PositionCallback,
      errorCallback?: PositionErrorCallback | null,
      options?: PositionOptions
    ): number => {
      const localWatchId = watchIdCounter++;
      
      Geolocation.watchPosition(
        {
          enableHighAccuracy: options?.enableHighAccuracy ?? true,
          timeout: options?.timeout ?? 10000,
          maximumAge: options?.maximumAge ?? 0,
        },
        (position, err) => {
          if (err) {
            console.error('[Geolocation] watchPosition error:', err);
            if (errorCallback) {
              errorCallback({
                code: GeolocationPositionError.POSITION_UNAVAILABLE,
                message: err.message || 'Position unavailable',
                PERMISSION_DENIED: GeolocationPositionError.PERMISSION_DENIED,
                POSITION_UNAVAILABLE: GeolocationPositionError.POSITION_UNAVAILABLE,
                TIMEOUT: GeolocationPositionError.TIMEOUT,
              } as GeolocationPositionError);
            }
            return;
          }

          if (position) {
            successCallback({
              coords: {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                altitude: position.coords.altitude,
                altitudeAccuracy: position.coords.altitudeAccuracy,
                heading: position.coords.heading,
                speed: position.coords.speed,
              },
              timestamp: position.timestamp,
            } as GeolocationPosition);
          }
        }
      ).then((callbackId) => {
        watchCallbacks.set(localWatchId, callbackId);
      });

      return localWatchId;
    },

    clearWatch: (watchId: number): void => {
      const callbackId = watchCallbacks.get(watchId);
      if (callbackId) {
        Geolocation.clearWatch({ id: callbackId });
        watchCallbacks.delete(watchId);
      }
    },
  } as unknown as Geolocation;

  // Override navigator.geolocation
  Object.defineProperty(navigator, 'geolocation', {
    value: capacitorGeolocation,
    writable: true,
    configurable: true,
  });

  console.log('[Geolocation] Navigator.geolocation polyfilled with Capacitor implementation');
}

/**
 * Check if geolocation permissions are granted
 */
export async function checkGeolocationPermission(): Promise<'granted' | 'denied' | 'prompt'> {
  if (!Capacitor.isNativePlatform()) {
    // On web, check using the Permissions API if available
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        return result.state;
      } catch {
        return 'prompt';
      }
    }
    return 'prompt';
  }

  try {
    const status = await Geolocation.checkPermissions();
    if (status.location === 'granted') return 'granted';
    if (status.location === 'denied') return 'denied';
    return 'prompt';
  } catch {
    return 'denied';
  }
}
